import type { IPage } from '../models/pages';
import { mergeClasses } from '../utils/mergeClasses';
import { EndpointData } from './EndpointData';
import { Resource } from './Resource';

type Props = {
  routeData: IPage;
};

/**
 * This is done so that when navigating and scrolling the page. There would be a pre-assigned height,
 * so that the page would not jitter when scrolling to the anchor that it targets.
 *
 * This is strictly done for UX reasons
 */
const DEFAULT_RESOURCE_PLACEHOLDER = 500;
const DEFAULT_ENDPOINT_PLACEHOLDER = 1200;

export const Page = ({ routeData }: Props) => {
  const ComponentToRender = routeData.article?.component;

  if (!routeData?.article)
    return <span>Error! Unable to load router data.</span>;

  return (
    <div data-testid={routeData.id}>
      <div className="flex w-full flex-row justify-center">
        <div
          id={routeData.article.id}
          className="m-4 flex w-full flex-col gap-4 p-4"
        >
          <p
            className={mergeClasses(
              /*
               * We check if this is a leaf node
               * If it is a leaf node, we show <h2>, if it's not we show <h1>
               * TODO: find a way to get exact depth when doing the resolvePages
               */
              routeData?.children ? 'text-4xl' : 'text-3xl',
              'font-bold'
            )}
            id={routeData.article.id}
          >
            {routeData.article.label}
          </p>

          {routeData.article.resource && (
            <Resource resource={routeData.article.resource} />
          )}

          {ComponentToRender ? (
            <ComponentToRender {...routeData.article.props} />
          ) : (
            <p className="text-lg leading-8">{routeData.article.content}</p>
          )}

          {routeData.article?.endpoint?.path && routeData.swaggerDoc && (
            <div
              key={routeData.id}
              style={{ minHeight: DEFAULT_ENDPOINT_PLACEHOLDER }}
            >
              <EndpointData
                id={routeData.id}
                swaggerDoc={routeData.swaggerDoc}
              />
            </div>
          )}
        </div>
      </div>
      {routeData.children?.map((child) => (
        <div key={child.id} style={{ minHeight: DEFAULT_RESOURCE_PLACEHOLDER }}>
          <Page routeData={child} />
        </div>
      ))}
    </div>
  );
};
